import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid2,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
} from "react";
import {
  Person as PersonIcon,
  Group as GroupIcon,
  Groups as GroupsIcon,
} from "@mui/icons-material";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
// import UploadImageForm from "../../../components/forms/UploadImageForm";
import UploadImageForm from "../../../components/forms/UploadImageFormPG";
import { useGetAmenitiesQuery } from "../../amenities/api/amenitiesApiSlice";

// 2. Icon Mapper Helper
const getUnitTypeIcon = (iconName) => {
  switch (iconName) {
    case "PersonIcon":
      return <PersonIcon fontSize="small" />;
    case "GroupIcon":
      return <GroupIcon fontSize="small" />;
    case "GroupsIcon":
      return <GroupsIcon fontSize="small" />;
    case "DeckIcon":
      return <GroupsIcon fontSize="small" />;
    default:
      return <PersonIcon fontSize="small" />;
  }
};

const UnitForm = forwardRef(
  (
    // { unitTypes, isLoading, isError, onDataChange, parent, initialData },
    { unitTypes, isLoading, onDataChange, initialData },
    ref,
  ) => {
    // const unitTypeOptions = useMemo(() => {
    //   if (unitTypes) {
    //     // Case A: RTK entity adapter shape { ids: [...], entities: { id: {...} } }
    //     if (unitTypes.entities && Object.keys(unitTypes.entities).length) {
    //       return Object.values(unitTypes.entities);
    //     }
    //   }
    //   return [];
    // }, [unitTypes]);

    const unitTypeOptions = useMemo(() => {
      if (unitTypes) {
        // Correct Industry Standard: Use 'ids' to iterate, 'entities' to lookup
        if (unitTypes.ids && unitTypes.entities) {
          return unitTypes.ids.map((id) => unitTypes.entities[id]);
        }

        // Fallback for raw array (if you ever switch data structures)
        if (Array.isArray(unitTypes)) {
          return unitTypes;
        }
      }
      return [];
    }, [unitTypes]);

    const { data: amenitiesData, isLoading: loadingAmenities } =
      useGetAmenitiesQuery();

    // 2. Memoized Filtering: Only show 'property' or 'both' categories in this form
    const buildingAmenities = useMemo(() => {
      if (!amenitiesData) return [];
      // Handle RTK Query entity pattern if you use it, else assume array
      const list = amenitiesData.ids
        ? amenitiesData.ids.map((id) => amenitiesData.entities[id])
        : amenitiesData;
      return list.filter((a) => a.category === "unit" || a.category === "both");
    }, [amenitiesData]);

    const BATHROOM_TYPES = [
      { id: "attached", label: "Attached Washroom" },
      { id: "common", label: "Common Washroom" },
      // { id: "shared", label: "Shared between 2-3 rooms" },
    ];

    // const FOOD_OPTIONS = [
    //   { id: "breakfast", label: "Breakfast" },
    //   { id: "lunch", label: "Lunch" },
    //   { id: "dinner", label: "Dinner" },
    //   { id: "tea", label: "Tea/Snacks" },
    //   { id: "all", label: "All Meals" },
    // ];

    // const ROOM_AMENITIES = [
    //   { id: "AC", label: "Air Conditioning" },
    //   { id: "TV", label: "Television" },
    //   { id: "Cupboard", label: "Wardrobe/Cupboard" },
    //   { id: "Table", label: "Study Table & Chair" },
    //   { id: "Mattress", label: "Mattress & Bedding" },
    //   { id: "Balcony", label: "Attached Balcony" },
    //   { id: "Geyser", label: "Water Heater" },
    //   { id: "Fan", label: "Ceiling Fan" },
    //   { id: "Locker", label: "Personal Locker" },
    //   { id: "WiFi", label: "Dedicated WiFi" },
    // ];

    // Refs
    const unitTypeRef = useRef(null);
    const bathroomRef = useRef(null);
    const sizeRef = useRef(null);
    const priceRef = useRef(null);
    const depositRef = useRef(null);

    // Errors
    const [errors, setErrors] = useState({});

    const uploadImageFormRef = useRef();
    const [uploadImage, setUploadImage] = useState({
      // unitImages: [],
      // unitMainImage: null,

      unitImages: initialData?.unitImages || [],
      unitMainImage: initialData?.unitMainImage || null,
    });

    // Form State
    const [formData, setFormData] = useState({
      unitType: initialData?.unitType || "", // Store the ObjectId
      bathroomType: initialData?.bathroomType || "",
      size: initialData?.size || "",
      // amenities: initialData?.amenities || [], // Array of ObjectIds
      amenities: (initialData.amenities || []).map((amenity) =>
        typeof amenity === "object" ? amenity.id || amenity._id : amenity,
      ),
      // foodIncluded: initialData?.foodIncluded || [],
      foodDetails: initialData?.foodDetails || {
        isAvailable: false,
        dietaryPreference: "veg",
        mealsProvided: [],
      },
      price: initialData?.price || "",
      securityDeposit: initialData?.securityDeposit || "",
      noticePeriod: initialData?.noticePeriod || "30",
      // startDate: initialData?.startDate
      //   ? dayjs(initialData.startDate)
      //   : dayjs(),
      unitImages: initialData?.unitImages || [],
      unitMainImage: initialData?.unitMainImage || null,
      comments: initialData?.comments || "",
    });

    // 1. Initial mount setup (runs once)
    useEffect(() => {
      // Run once on mount: initialize the state from initialData
      if (initialData) {
        setFormData({
          ...initialData,
          amenities: (initialData.amenities || []).map((amenity) =>
            typeof amenity === "object" ? amenity.id || amenity._id : amenity,
          ),
          // startDate: initialData.startDate
          //   ? dayjs(initialData.startDate)
          //   : null,
          // endDate: initialData.endDate ? dayjs(initialData.endDate) : null,
        });

        setUploadImage({
          mainImage: formData.unitMainImage || null,
          images: formData.unitImages || [],
        });
      }
    }, []);

    // useEffect(() => {
    //   // if the user already chose a unitType that no longer exists in the newly‐fetched list,
    //   // clear it so the Select falls back to “-- Select --”
    //   if (formData.unitType && !isLoading) {
    //     const hasUnitType = Object.values(unitTypes?.entities ?? {}).some(
    //       (ut) => ut.id === formData.unitType
    //     );
    //     if (!hasUnitType) {
    //       setFormData((prev) => ({ ...prev, unitType: "" }));
    //     }
    //   }
    // }, [unitTypes]);

    // Handle Changes
    const handleChange = (e) => {
      const { name, value } = e.target;
      setErrors((prev) => ({ ...prev, [name]: "" }));

      const updatedData = { ...formData, [name]: value };
      setFormData(updatedData);
      onDataChange(updatedData);
    };

    const handleDataChange = (newData) => {
      const updatedData = { ...formData, ...newData };
      setFormData(updatedData);
      onDataChange(updatedData);

      Object.keys(newData).forEach((key) => {
        if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
      });
    };

    // // Handle Arrays (Amenities/Food)
    // const handleArrayToggle = (category, value) => {
    //   setFormData((prev) => {
    //     const list = prev[category];
    //     const newList = list.includes(value)
    //       ? list.filter((i) => i !== value)
    //       : [...list, value];

    //     // Handle "All Meals" selection
    //     if (category === "foodIncluded") {
    //       if (value === "all") {
    //         if (newList.includes("all")) {
    //           // Select all individual meals when "All Meals" is checked
    //           return {
    //             ...prev,
    //             foodIncluded: ["breakfast", "lunch", "dinner", "tea", "all"],
    //           };
    //         } else {
    //           // Remove all when "All Meals" is unchecked
    //           return { ...prev, foodIncluded: [] };
    //         }
    //       } else if (newList.includes("all")) {
    //         // If any individual meal is unchecked, remove "All Meals"
    //         const filtered = newList.filter((item) => item !== "all");
    //         return { ...prev, foodIncluded: filtered };
    //       }
    //     }

    //     const updated = { ...prev, [category]: newList };
    //     onDataChange(updated);
    //     return updated;
    //   });
    // };

    // Handle Checkbox Changes (Amenities)
    const handleCheckboxChange = (category, id) => {
      setFormData((prev) => {
        const currentList = prev[category];
        const newList = currentList.includes(id)
          ? currentList.filter((item) => item !== id)
          : [...currentList, id];

        const updatedData = { ...prev, [category]: newList };
        onDataChange(updatedData);
        return updatedData;
      });
    };

    // const handleFoodChange = (field, value) => {
    //   const updatedFood = { ...formData.foodDetails, [field]: value };
    //   setFormData({ ...formData, foodDetails: updatedFood });
    //   onDataChange({ ...formData, foodDetails: updatedFood });
    // };
    const handleFoodChange = (field, value) => {
      setFormData((prev) => {
        const updatedFood = {
          ...prev.foodDetails,
          [field]: value,
        };
        const updatedData = { ...prev, foodDetails: updatedFood };

        // Always trigger the callback with the latest data
        onDataChange(updatedData);
        return updatedData;
      });
    };

    // // Handle Arrays (Amenities/Food)
    // const handleArrayToggle = (category, value) => {
    //   let newList;

    //   if (category === "foodIncluded") {
    //     if (value === "all") {
    //       // Toggle "All Meals"
    //       const hasAll = formData.foodIncluded.includes("all");
    //       newList = hasAll
    //         ? []
    //         : ["breakfast", "lunch", "dinner", "tea", "all"];
    //     } else {
    //       // Toggle individual meal
    //       const list = formData.foodIncluded;
    //       const hasItem = list.includes(value);

    //       if (hasItem) {
    //         newList = list.filter((item) => item !== value && item !== "all");
    //       } else {
    //         newList = [...list, value];
    //         // Remove "all" if we're adding individual meals
    //         newList = newList.filter((item) => item !== "all");
    //       }
    //     }
    //   } else {
    //     // For amenities
    //     const list = formData[category];
    //     newList = list.includes(value)
    //       ? list.filter((item) => item !== value)
    //       : [...list, value];
    //   }

    //   const updatedData = { ...formData, [category]: newList };
    //   setFormData(updatedData);
    //   onDataChange(updatedData);
    // };

    // // Date Handler
    // const handleDateChange = (newValue) => {
    //   const updated = { ...formData, startDate: newValue };
    //   setFormData(updated);
    //   onDataChange(updated);
    // };

    // Validation
    const validateUserInput = () => {
      const newErrors = {};
      let isValid = true;

      // 1. Room Type
      if (!formData.unitType) {
        newErrors.unitType = "Room type is required";
        isValid = false;
      }

      if (!formData.bathroomType) {
        newErrors.bathroomType = "Bathroom type is required";
        isValid = false;
      }

      if (
        !formData.size ||
        isNaN(formData.size) ||
        Number(formData.size) <= 0
      ) {
        newErrors.size = "Valid room size is required";
        isValid = false;
      }

      // 2. Price (Rent)
      if (
        !formData.price ||
        isNaN(formData.price) ||
        Number(formData.price) <= 0
      ) {
        newErrors.price = "Valid rent amount is required";
        isValid = false;
      }

      // 3. Security Deposit
      if (
        !formData.securityDeposit ||
        isNaN(formData.securityDeposit) ||
        Number(formData.securityDeposit) < 0
      ) {
        newErrors.securityDeposit = "Valid deposit amount is required";
        isValid = false;
      }

      // Validate images
      // if (!uploadImage.mainImage) {
      //   uploadImageFormRef.current?.validateUserInput();
      //   // return false;
      //   isValid = false;
      // }

      setErrors(newErrors);

      // Focus first error
      if (!isValid) {
        if (newErrors.unitType && unitTypeRef.current) {
          unitTypeRef.current.focus();
        } else if (newErrors.bathroomType && bathroomRef.current) {
          bathroomRef.current.focus();
        } else if (newErrors.size && sizeRef.current) {
          sizeRef.current.focus();
        } else if (newErrors.price && priceRef.current) {
          priceRef.current.focus();
        } else if (newErrors.securityDeposit && depositRef.current) {
          depositRef.current.focus();
        }

        return false;
      }

      // Validate images
      if (!uploadImage.mainImage) {
        uploadImageFormRef.current?.validateUserInput();
        // return false;
        isValid = false;
      }

      return isValid;

      // let imagesValid = true;
      // if (uploadImageFormRef.current?.validateUserInput) {
      //   try {
      //     imagesValid = uploadImageFormRef.current.validateUserInput();
      //   } catch (err) {
      //     // In case child throws, treat as invalid but don't crash
      //     imagesValid = false;
      //   }
      // }

      // // If images invalid, let the child handle its own focusing/UX.
      // if (!imagesValid) return false;
    };

    // Callback function that is triggered when UploadImageForm data changes
    const handleUploadDataChange = (data) => {
      setUploadImage(data); // Update local state

      // Update formData with new images while preserving other fields
      setFormData((prevData) => ({
        ...prevData,
        unitMainImage: data.mainImage, // Update unitMainImage
        unitImages: data.images, // Update unitImages
      }));

      // Notify parent of data changes
      onDataChange({
        ...formData,
        unitMainImage: data.mainImage, // Pass updated main image
        unitImages: data.images, // Pass updated images array
      });
    };

    // useImperativeHandle(ref, () => ({
    //   validateUserInput,
    //   getFormData: () => formData,
    // }));

    // Expose the validate method to the parent via ref
    useImperativeHandle(ref, () => ({
      validateUserInput,
      // To fetch all form data from UnitForm:
      getFormData: () => ({
        ...formData,
        unitImages: uploadImage.images,
        unitMainImage: uploadImage.mainImage,
      }),
    }));

    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 800,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Room Configuration Section */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Room Configuration
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth error={!!errors.unitType} required>
                <InputLabel>Room Type</InputLabel>
                <Select
                  name="unitType"
                  // value={formData.sharingType}
                  value={formData.unitType || ""}
                  onChange={(e) =>
                    handleDataChange({ unitType: e.target.value })
                  }
                  inputRef={unitTypeRef}
                  label="Room Type"
                >
                  <MenuItem value="">-- Select Room Type --</MenuItem>
                  {unitTypeOptions.map((unitType) => (
                    <MenuItem
                      key={unitType._id || unitType.id}
                      value={unitType._id || unitType.id}
                    >
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        {getUnitTypeIcon(unitType.icon)}
                        {unitType.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.unitType}</FormHelperText>
              </FormControl>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth error={!!errors.bathroomType} required>
                <InputLabel>Bathroom Type</InputLabel>
                <Select
                  name="bathroomType"
                  value={formData.bathroomType}
                  onChange={handleChange}
                  inputRef={bathroomRef}
                  label="Bathroom Type"
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {BATHROOM_TYPES.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.bathroomType}</FormHelperText>
              </FormControl>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Room Size"
                name="size"
                value={formData.size}
                onChange={handleChange}
                inputRef={sizeRef}
                error={!!errors.size}
                helperText={errors.size}
                required
                type="number"
                slotProps={{
                  htmlInput: {
                    min: 50, // Match Schema
                    max: 5000, // Match Schema
                    // step: 50,
                    // onKeyDown: (e) => {
                    //   // Prevent "e", "E", "+", and "-" from being entered
                    //   if (["e", "E", "+", "-"].includes(e.key)) {
                    //     e.preventDefault();
                    //   }
                    // },
                    onKeyDown: (e) =>
                      ["e", "E", "+", "-"].includes(e.key) &&
                      e.preventDefault(),
                  },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">sq ft</InputAdornment>
                    ),
                  },
                }}
                // helperText="Number of days notice required before vacating"
              />
            </Grid2>
          </Grid2>
        </Paper>

        {/* Room Amenities Section */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Room Amenities
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{ mb: 1 }}
          >
            Select amenities available in this room
          </Typography>
          <Grid2 container spacing={1}>
            {buildingAmenities.map((amenity) => (
              <Grid2
                key={amenity.id || amenity._id}
                size={{ xs: 6, sm: 4, md: 3 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData?.amenities.includes(
                        amenity.id || amenity._id,
                      )}
                      onChange={() =>
                        handleCheckboxChange(
                          "amenities",
                          amenity.id || amenity._id,
                        )
                      }
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">{amenity.name}</Typography>
                  }
                />
              </Grid2>
            ))}
          </Grid2>
        </Paper>

        {/* Food & Meals Section */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Food & Meals
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{ mb: 1 }}
          >
            Select meals included in the rent
          </Typography>

          {/* Main Toggle */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.foodDetails.isAvailable}
                onChange={(e) =>
                  handleFoodChange("isAvailable", e.target.checked)
                }
                size="small"
              />
            }
            // label="Is Food Provided?"
            label={<Typography variant="body2">Is Food Provided?</Typography>}
          />

          {formData.foodDetails?.isAvailable && (
            <>
              <Grid2 container spacing={1} sx={{ mt: 3 }}>
                {/* Dietary Preference */}
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Dietary Preference</InputLabel>
                    <Select
                      value={formData.foodDetails?.dietaryPreference}
                      onChange={(e) =>
                        handleFoodChange("dietaryPreference", e.target.value)
                      }
                      label="Dietary Preference"
                    >
                      <MenuItem value="veg">Pure Veg</MenuItem>
                      <MenuItem value="non-veg">Non-Veg Allowed</MenuItem>
                      <MenuItem value="both">Both Veg & Non-Veg</MenuItem>
                    </Select>
                  </FormControl>
                </Grid2>
              </Grid2>

              {/* Meals Provided Checkboxes */}

              <Grid2 size={{ xs: 12 }}>
                <FormLabel component="legend">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ mt: 3 }}
                  >
                    Meals Included
                  </Typography>
                  {/* Meals Included */}
                </FormLabel>
              </Grid2>

              <Grid2 container spacing={1}>
                {["breakfast", "lunch", "dinner"].map((meal) => (
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControlLabel
                      key={meal}
                      control={
                        <Checkbox
                          checked={
                            formData.foodDetails?.mealsProvided?.includes(
                              meal,
                            ) || false
                          }
                          onChange={(e) => {
                            const currentMeals =
                              formData.foodDetails?.mealsProvided || [];
                            const newMeals = e.target.checked
                              ? [...currentMeals, meal]
                              : currentMeals.filter((m) => m !== meal);
                            handleFoodChange("mealsProvided", newMeals);
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {meal.charAt(0).toUpperCase() + meal.slice(1)}
                        </Typography>
                      }
                    />
                  </Grid2>
                ))}
              </Grid2>
              {/* </Grid2> */}
              {/* </Grid2> */}
            </>
          )}

          {/* <Grid2 container spacing={1}>
            {FOOD_OPTIONS.map((item) => (
              <Grid2 key={item.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.foodIncluded.includes(item.id)}
                      onChange={() =>
                        handleArrayToggle("foodIncluded", item.id)
                      }
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{item.label}</Typography>}
                />
              </Grid2>
            ))}
          </Grid2> */}
        </Paper>

        {/* Financial Details Section */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Financial Details
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Monthly Rent per Bed"
                name="price"
                value={formData.price}
                onChange={handleChange}
                inputRef={priceRef}
                required
                type="number"
                error={!!errors.price}
                helperText={errors.price}
                // InputProps={{
                //   startAdornment: (
                //     <Typography
                //       sx={{ mr: 1, color: "text.secondary", fontSize: 17 }}
                //     >
                //       ₹
                //     </Typography>
                //   ),
                // }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            color: "text.secondary",
                            mt: 0.5,
                            fontSize: 17,
                          }}
                        >
                          {/* ₹ */}
                          <CurrencyRupeeIcon fontSize="inherit" />
                        </Typography>
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    min: 0,
                    step: 500,
                  },
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Security Deposit"
                name="securityDeposit"
                value={formData.securityDeposit}
                onChange={handleChange}
                inputRef={depositRef}
                required
                type="number"
                error={!!errors.securityDeposit}
                helperText={errors.securityDeposit}
                // InputProps={{
                //   startAdornment: (
                //     <Typography
                //       sx={{ mr: 1, color: "text.secondary", fontSize: 17 }}
                //     >
                //       ₹
                //     </Typography>
                //   ),
                // }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            color: "text.secondary",
                            mt: 0.5,
                            fontSize: 17,
                          }}
                        >
                          {/* ₹ */}
                          <CurrencyRupeeIcon fontSize="inherit" />
                        </Typography>
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    min: 0,
                    step: 1000,
                  },
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Notice Period (Days)"
                name="noticePeriod"
                value={formData.noticePeriod}
                onChange={handleChange}
                type="number"
                slotProps={{
                  htmlInput: {
                    min: 0,
                    max: 180,
                  },
                }}
                helperText="Number of days notice required before vacating"
              />
            </Grid2>
          </Grid2>
        </Paper>

        {/* Upload Image Section */}
        {/* <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Room Interior Photos
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ mb: 2 }}
        >
          Upload photos of this specific room
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <UploadImageForm
            ref={uploadImageFormRef}
            onDataChange={handleUploadDataChange}
            imageInfo={"Room interior photos *"}
            // initialData={uploadImage}
            initialData={{
              images: initialData?.unitImages || [],
              mainImage: initialData?.unitMainImage || null,
            }}
          />
        </Box>
      </Paper> */}

        {/* 6. Upload Image Field */}
        {/* <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
        }}
      > */}
        <UploadImageForm
          ref={uploadImageFormRef}
          onDataChange={handleUploadDataChange}
          imageInfo={"Room Interior Photos"}
          initialData={{
            images: initialData?.unitImages || [],
            mainImage: initialData?.unitMainImage || null,
          }}
        />
        {/* </Box> */}

        {/* Availability Section */}
        {/* <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Availability
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <DatePicker
                label="Available From"
                value={formData.startDate}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText:
                      "Date from which the room is available for occupancy",
                  },
                }}
              />
            </Grid2>
          </Grid2>
        </LocalizationProvider>
      </Paper> */}

        {/* Additional Information Section */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Additional Information
          </Typography>
          <TextField
            fullWidth
            label="Room Description & Additional Details"
            name="comments"
            multiline
            rows={4}
            value={formData.comments}
            onChange={handleChange}
            placeholder="Describe the room, special features, any additional terms, etc."
            helperText={`${formData.comments.length}/500 characters`}
            slotProps={{
              htmlInput: {
                maxLength: 500,
              },
            }}
          />
        </Paper>
      </Box>
    );
  },
);

export default UnitForm;
